import React, { useState } from 'react';
import { Check, Star, Globe, MessageSquare, Heart, Crown } from 'lucide-react';

const PricingPlans = () => {
  const [billingRegion, setBillingRegion] = useState('local');
  
  const plans = [
    {
      name: 'Free Basic',
      duration: '15 Days',
      visibility: 'Standard',
      features: [
        'Profile creation',
        '1 proposal per day',
        'Limited filters',
        'Email alerts'
      ],
      localPrice: 'Free',
      overseasPrice: 'Free',
      icon: Heart,
      color: 'from-gray-400 to-gray-600',
      popular: false
    },
    {
      name: 'Verified Standard',
      duration: '2 months',
      visibility: 'Standard',
      features: [
        'Verified badge',
        '3 daily proposals',
        'All basic features',
        'Priority support'
      ],
      localPrice: '4000',
      overseasPrice: '50.00',
      icon: Check,
      color: 'from-blue-500 to-blue-700',
      popular: false
    },
    {
      name: 'Premium Match',
      duration: '4 months',
      visibility: 'Standard + Limited Featured',
      features: [
        'All Standard features',
        '5 daily proposals',
        'Video intro profile',
        'WhatsApp support'
      ],
      localPrice: '7500',
      overseasPrice: '75.00',
      icon: Star,
      color: 'from-purple-500 to-purple-700',
      popular: true
    },
    {
      name: 'Elite Sponsored',
      duration: '6 months',
      visibility: 'Limited Featured + Global Visibility',
      features: [
        '7 daily proposals',
        'Profile in global list',
        'Direct inbox access',
        'Top search ranking',
        'WhatsApp support',
        'Exciting features coming'
      ],
      localPrice: '12000',
      overseasPrice: '95.00',
      icon: Crown,
      color: 'from-gradient-to-r from-yellow-400 via-orange-500 to-red-500',
      popular: false
    }
  ];

  const formatPrice = (localPrice, overseasPrice) => {
    if (localPrice === 'Free') return 'Free';
    
    if (billingRegion === 'local') {
      return `₨${localPrice}`;
    } else {
      return `$${overseasPrice}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {/* <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Check className="w-4 h-4" />
            Select Proposal Pricing Plans
          </div> */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#730000] mb-4">
            Choose Your Perfect Plan
          </h1>
          <p className="text-2xl max-w-3xl mx-auto">
            Find your ideal match with our comprehensive proposal plans designed for every budget and requirement.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1 rounded-xl shadow-md border-2 border-[#730000]">
            <button
              onClick={() => setBillingRegion('local')}
              className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                billingRegion === 'local'
                  ? 'bg-[#730000] text-white shadow-lg'
                  : 'text-gray-600 hover:text-[#730000]'
              }`}
            >
              Local (PKR)
            </button>
            <button
              onClick={() => setBillingRegion('overseas')}
              className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                billingRegion === 'overseas'
                  ? 'bg-[#730000] text-white shadow-lg'
                  : 'text-gray-600 hover:text-[#730000]'
              }`}
            >
              Overseas (USD)
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, index) => {
            const IconComponent = plan.icon;
            return (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-xl hover:shadow-[#730000] hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${
                  plan.popular ? 'ring-2 ring-purple-500 scale-105' : ''
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r whitespace-nowrap from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Card Content */}
                <div className="p-6">
                  <div className='grid grid-row-1'>

                  {/* Icon and Title */}
                  <div className="text-center h-[160px] mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${plan.color} mb-4`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#730000] mb-2">{plan.name}</h3>
                  </div>
                    <div className="text-gray-600 h-[80px] text-center">
                      <p className="text-sm">Access Duration: <span className="font-semibold">{plan.duration}</span></p>
                      <p className="text-sm mt-1">Visibility: <span className="font-semibold">{plan.visibility}</span></p>
                    </div>

                  {/* Price */}
                  <div className="text-center mb-8 ">
                    <div className="text-4xl font-bold text-[#730000]">
                      {formatPrice(plan.localPrice, plan.overseasPrice)}
                    </div>
                    {/* {plan.localPrice !== 'Free' && (
                      <p className="text-gray-500 text-sm mt-1">per {plan.duration.toLowerCase()}</p>
                    )} */}
                  </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Info */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">
            Need help choosing the right plan? Our support team is here to help.
          </p>
          <div className="flex justify-center items-center gap-8 max-sm:gap-2 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              24/7 Support
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Global Reach
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Verified Profiles
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;